import {CompanyModel} from './company.model';
import {RequestedProductInfoModel} from './requested-product-info.model';
import {RequestedSupplyInfoModel} from './requested-supply-info.model';
import {TenderDocumentModel} from './tender-document.model';
import {EnumUtils} from '../utils/enum.utils';
import {SupplyAcquisitionTypeEnum} from '../enums/supply-acquisition-type.enum';
import {RequestedNotDeviceItemModel} from './requested-not-device-item.model';
import {RequestedDeviceItemModel} from './requested-device-item.model';
import {DateTimeUtils} from '../utils/date-time.utils';
import {AccountModel} from './account.model';
import {BrotherContactsModel} from './brother-contacts.model';
import {OfferTextModuleModel} from './offer-text-module.model';
import {ParentSkuMappingItemModel} from './parent-sku-mapping-item.model';
import {MyDialogDataModel} from './my-dialog-data.model';
import {MyProductRPricesSelectBaseEnum} from '../enums/my-product-rprices-select-base.enum';
import {ProjectStatusDetailsEnum} from '../enums/project-status-details.enum';
import {ObjectsUtils} from '../utils/objects.utils';
import {AcquisitionTypeEnum} from '../enums/acquisition-type.enum';
import {ProjectStatusEnum} from '../enums/project-status.enum';

export class ProjectModel {
    id: number;
    sapProjectId: string;
    projectName: string;
    account: AccountModel;
    industrySector: string; // IndustrySectorEnum;
    department: string;
    employeeCountRange: string; // ProjectEmployeeCountRangeEnum;
    referenceNo: string;
    company: CompanyModel;
    quoteClosingDate: Date;
    quoteValidUntil: Date;
    comment: string;
    internalComment: string;
    isTender: boolean;
    tenderDocuments: TenderDocumentModel[];
    brotherContacts: BrotherContactsModel;

    status: string; // ProjectStatusEnum;
    statusDetails: string; // ProjectStatusDetailsEnum;
    priceApprovalStatus: string; // PriceApprovalStatusEnum;

    commentHardware: string;
    commentSupplies: string;

    offerTextModules: OfferTextModuleModel[];
    hardwareAcquisitionType: string; // AcquisitionTypeEnum
    accessoryAcquisitionType: string; // AcquisitionTypeEnum
    warrantyAcquisitionType: string;  // AcquisitionTypeEnum
    serviceAcquisitionType: string;  // AcquisitionTypeEnum
    upgradeAcquisitionType: string;  // AcquisitionTypeEnum

    requestedProductInfo: RequestedProductInfoModel;

    supplyAcquisitionType: string; // SupplyAcquisitionTypeEnum;
    requestedSupplyInfo: RequestedSupplyInfoModel;

    updateNotificationDetails: string[]; // ProjectUpdateNotificationField[];

    validityOfQuotation: string;  // actually is the date type, but is not used in dealer app yet, just must be removed before the project will be saved.

    public static getEmptyProject(): ProjectModel {
        return {
            projectName: '',
            industrySector: '', // IndustrySectorEnum;
            department: '',
            employeeCountRange: '', // ProjectEmployeeCountRangeEnum,
            company: new CompanyModel(),
            quoteClosingDate: null,
            comment: '',
            status: EnumUtils.getKeyByValue(ProjectStatusEnum, ProjectStatusEnum.DRAFT),
            isTender: null,
            tenderDocuments: [],

            requestedProductInfo: new RequestedProductInfoModel(),

            supplyAcquisitionType: EnumUtils.getKeyByValue(SupplyAcquisitionTypeEnum, SupplyAcquisitionTypeEnum.NONE),
            requestedSupplyInfo: new RequestedSupplyInfoModel(),


            // internalComment: '',

        } as ProjectModel;
    }


    public static fromJson(project: any): ProjectModel {

        project.quoteClosingDate = DateTimeUtils.StringToDate(project.quoteClosingDate);
        project.quoteValidUntil = DateTimeUtils.StringToDate(project.quoteValidUntil);
        project.requestedProductInfo = RequestedProductInfoModel.fromJson(project.requestedProductInfo);
        project.requestedSupplyInfo = RequestedSupplyInfoModel.fromJson(project.requestedSupplyInfo);


        return project as ProjectModel;
    }


    public static toJson(project: ProjectModel): object {

        const newProject: any = {...project};

        newProject.quoteClosingDate = DateTimeUtils.DateToRestString(project.quoteClosingDate);
        newProject.quoteValidUntil = DateTimeUtils.DateToRestString(project.quoteValidUntil);
        newProject.requestedProductInfo = RequestedProductInfoModel.toJson(newProject.requestedProductInfo);
        newProject.requestedSupplyInfo = RequestedSupplyInfoModel.toJson(newProject.requestedSupplyInfo);

        if (newProject.tenderDocuments) {
            newProject.tenderDocuments = ObjectsUtils.deepCopy(newProject.tenderDocuments);
        }
        if (newProject.offerTextModules) {
            newProject.offerTextModules = ObjectsUtils.deepCopy(newProject.offerTextModules);
        }

        return newProject;
    }


    public static updateProjectModelForConsistence(project: ProjectModel, isInternalUser = false) {
        ProjectModel.updateRequestedSupplyInfoExistence(project);

        if (project.requestedSupplyInfo) {
            if (!ProjectModel.isPrintSmartSupplyAcquisitionType(project)) {
                delete project.requestedSupplyInfo.timePeriodInMonths;
            } else {
                delete project.requestedSupplyInfo.rolloutEnd;
            }

            if (project.requestedProductInfo?.requestedProducts?.length === 0) {
                delete project.requestedProductInfo.rolloutEnd;
                delete project.requestedProductInfo.rolloutStart;
            }
        }


        if (!project.supplyAcquisitionType) {
            project.supplyAcquisitionType = EnumUtils.getKeyByValue(SupplyAcquisitionTypeEnum, SupplyAcquisitionTypeEnum.NONE);
        }


        ProjectModel.updateAmountSums(project);

        if (isInternalUser && !project.statusDetails) {
            project.statusDetails = EnumUtils.getKeyByValue(ProjectStatusDetailsEnum, ProjectStatusDetailsEnum.NONE);
        }

        if (!project.isTender){
            project.tenderDocuments = [];
        }


    }

    public static isPrintSmartSupplyAcquisitionType(project: ProjectModel) {
        return project.supplyAcquisitionType &&
            (SupplyAcquisitionTypeEnum[project.supplyAcquisitionType] === SupplyAcquisitionTypeEnum.MPS_BASED_ON_PAGE_PRICE
                || SupplyAcquisitionTypeEnum[project.supplyAcquisitionType] === SupplyAcquisitionTypeEnum.MPS_BASED_ON_UNIT_COUNT);
    }

    public static updateAmountSums(project: ProjectModel) {

        if (project.requestedProductInfo?.requestedProducts) {

            project.requestedProductInfo.requestedProducts.map(requestedProduct => {
                if ((requestedProduct as RequestedNotDeviceItemModel).parentSkuMapping?.length) {

                    console.warn('parentSkuMapping found in notSupplyProduct, it has not sense for last app layout');
                    const parentSkuMapping = (requestedProduct as RequestedNotDeviceItemModel).parentSkuMapping;
                    requestedProduct.amount = 0;
                    if (parentSkuMapping?.length) {
                        parentSkuMapping.forEach(psm => {
                            if (psm.amount === null || psm.amount === undefined) {
                                psm.amount = 0;
                            }
                            requestedProduct.amount += psm.amount;
                        });
                    }

                } else {
                    requestedProduct.amount = requestedProduct.amount ? requestedProduct.amount : 0;
                }
                return requestedProduct;
            });

        }

        if (project.requestedSupplyInfo?.requestedSupplies) {
            project.requestedSupplyInfo.requestedSupplies.map(requestedSupply => {

                if ( (requestedSupply as RequestedNotDeviceItemModel).parentSkuMapping?.length) {
                    const parentSkuMapping = (requestedSupply as RequestedNotDeviceItemModel).parentSkuMapping;
                    requestedSupply.amount = 0;
                    if (parentSkuMapping?.length) {
                        parentSkuMapping.forEach(psm => {
                            if (psm.amount === null || psm.amount === undefined) {
                                psm.amount = 0;
                            }
                            requestedSupply.amount += psm.amount;
                        });
                    }
                } else {
                    // parentSkuMapping was not found for supply item, it is possible for noDeviceParent supplies
                    requestedSupply.amount = requestedSupply.amount ? requestedSupply.amount : 0;
                }
                return requestedSupply;
            });
        }

        // console.log('projects AmountSums updated', project);
    }

    public static updateRequestedSupplyInfoExistence(project: ProjectModel) {

        if (project.supplyAcquisitionType && SupplyAcquisitionTypeEnum[project.supplyAcquisitionType] === SupplyAcquisitionTypeEnum.NONE) {
            // delete project.requestedSupplyInfo;
        }
    }

    public static clearProjectForDealerAppRest(tmpProject: any) {

        const typedProject = tmpProject as ProjectModel;
        typedProject.requestedProductInfo.requestedProducts.forEach(request => {
            delete request.priceInfo;
        });
        if (typedProject.requestedSupplyInfo?.requestedSupplies) {
            typedProject.requestedSupplyInfo.requestedSupplies.forEach(request => {
                delete request.priceInfo;
                delete request.pricePerPage;

                if (SupplyAcquisitionTypeEnum[typedProject.supplyAcquisitionType] === SupplyAcquisitionTypeEnum.MPS_BASED_ON_PAGE_PRICE) {
                    delete request.amount;
                }
            });
        }


        // console.log('clearProjectForDealerAppRest');

        if (tmpProject.supplyAcquisitionType === EnumUtils.getKeyByValue(SupplyAcquisitionTypeEnum, SupplyAcquisitionTypeEnum.NONE)) {
            tmpProject.requestedSupplyInfo = null;
        }

        if (tmpProject.requestedSupplyInfo?.requestedSupplies?.length === 0
        ) {
            tmpProject.requestedSupplyInfo = null;
            tmpProject.supplyAcquisitionType = 'NONE';
        }


        if (tmpProject.requestedProductInfo?.requestedProducts?.length === 0) {

            tmpProject.requestedProductInfo = null;
            tmpProject.hardwareAcquisitionType = EnumUtils.getKeyByValue(AcquisitionTypeEnum, AcquisitionTypeEnum.NONE);
            tmpProject.accessoryAcquisitionType = EnumUtils.getKeyByValue(AcquisitionTypeEnum, AcquisitionTypeEnum.NONE);
            tmpProject.warrantyAcquisitionType = EnumUtils.getKeyByValue(AcquisitionTypeEnum, AcquisitionTypeEnum.NONE);
            tmpProject.upgradeAcquisitionType = EnumUtils.getKeyByValue(AcquisitionTypeEnum, AcquisitionTypeEnum.NONE);
            tmpProject.serviceAcquisitionType = EnumUtils.getKeyByValue(AcquisitionTypeEnum, AcquisitionTypeEnum.NONE);
        }

        if (tmpProject.requestedProductInfo?.isOneshot) {
            tmpProject.requestedProductInfo.rolloutEnd = null;
        }


        if (!tmpProject.serviceAcquisitionType || tmpProject.serviceAcquisitionType === AcquisitionTypeEnum[AcquisitionTypeEnum.NONE]) {
            if (tmpProject.requestedProductInfo && tmpProject.requestedProductInfo.requestedServiceComment) {
                tmpProject.requestedProductInfo.requestedServiceComment = null;
            }
        }

        // if (tmpProject.requestedProductInfo && tmpProject.requestedProductInfo.requestedServiceComment ) {
        //     tmpProject.serviceAcquisitionType = AcquisitionTypeEnum[AcquisitionTypeEnum.PURCHASE];
        // }


        delete typedProject.quoteValidUntil;

        delete typedProject.statusDetails;

        if (!Number.isFinite(typedProject.id)) {
            // id not exist, going to use createProject or createProjectDraft which do not accept the status field
            delete typedProject.status;
        }


        if (typedProject.tenderDocuments) {
            typedProject.tenderDocuments.forEach(tenderDocument => {
                delete tenderDocument.id;
            });
        }

        if (tmpProject.offerTextModules) {
            tmpProject.offerTextModules.forEach(offerTextModule => {
                delete offerTextModule.id;
            });
        }

        delete tmpProject.validityOfQuotation;

    }

    public static clearProjectForManagementAppRest(tmpProject: any) {
        if (tmpProject.brotherContacts) {
            tmpProject.brotherContacts = {...tmpProject.brotherContacts};
            if (tmpProject.brotherContacts.kam) {
                tmpProject.brotherContacts.kam = {...tmpProject.brotherContacts.kam};
                delete tmpProject.brotherContacts.kam.kamContacts;
            }
            // if (tmpProject.brotherContacts.vl) {
            //     delete tmpProject.brotherContacts.vl;
            // }
            if (tmpProject.brotherContacts.innendienst) {
                tmpProject.brotherContacts.innendienst = {...tmpProject.brotherContacts.innendienst};
                delete tmpProject.brotherContacts.innendienst.innendienstContacts;
            }
        }

        if (tmpProject.requestedProductInfo && tmpProject.requestedProductInfo.requestedProducts) {
            const requestedProductsCopy: (RequestedDeviceItemModel | RequestedNotDeviceItemModel) [] = [];
            tmpProject.requestedProductInfo.requestedProducts.forEach(requestedProduct => {
                const requestedProductCopy = {...requestedProduct};
                // @ts-ignore
                delete requestedProductCopy.isPrintSmart;

                requestedProductCopy.priceInfo = {...requestedProductCopy.priceInfo};
                delete requestedProductCopy.priceInfo.rPriceInfo;

                // tslint:disable:max-line-length
                if (MyProductRPricesSelectBaseEnum[requestedProductCopy.priceInfo.selectedPrice] !== MyProductRPricesSelectBaseEnum.CUSTOM) {
                    delete requestedProductCopy.priceInfo.customPriceInfo;
                }

                delete requestedProductCopy.parentSkuMapping;

                requestedProductsCopy.push(requestedProductCopy);
            });
            tmpProject.requestedProductInfo.requestedProducts = requestedProductsCopy;
        }


        if (tmpProject.requestedSupplyInfo?.requestedSupplies) {
            tmpProject.requestedSupplyInfo.requestedSupplies.forEach(request => {
                if (SupplyAcquisitionTypeEnum[(tmpProject as ProjectModel).supplyAcquisitionType] === SupplyAcquisitionTypeEnum.MPS_BASED_ON_PAGE_PRICE) {
                    delete request.amount;
                }
            });
        }


        delete tmpProject.sapProjectId;
        delete tmpProject.account;
        delete tmpProject.comment;
        delete tmpProject.isTender;
        delete tmpProject.tenderDocuments;
        delete tmpProject.updateNotificationDetails;
        delete tmpProject.statusDetails;


        if (tmpProject.offerTextModules) {
            tmpProject.offerTextModules.forEach(offerTextModule => {
                delete offerTextModule.id;
            });
        }

        // if (tmpProject.requestedSupplyInfo && tmpProject.requestedSupplyInfo.requestedSupplies) {
        //     tmpProject.requestedSupplyInfo.requestedSupplies.forEach(supply => {
        //         if (supply.pricePerPage) {
        //             delete supply.pricePerPage.developStatus;
        //         }
        //     });
        // }


    }


    // project model itself doesnt contains enough info to confirm project validity,
    // so dialogModel must be used to collect missing data in fastest way
    public static isProjectInvalid(project: ProjectModel, dialogModel: MyDialogDataModel): { errorCode: number, errorMessage: string } {
        if (!(project.projectName && project.industrySector && project.department && project.employeeCountRange)) {
            return {
                errorCode: 10,
                errorMessage: 'some of required fields is missing'
            };
        }

        const company = project.company;
        if (!(company && company.name && company.street && company.zip && company.city && company.country)) {
            return {
                errorCode: 20,
                errorMessage: 'some of company fields is missing'
            };
        }

        if (!project.isTender && project.tenderDocuments?.length > 0) {
            return {
                errorCode: 30,
                errorMessage: 'tender documents must be empty'
            };
        }

        if (project?.requestedProductInfo?.requestedProducts?.length > 0) {
            const requestedProductInfoError = ProjectModel.checkRolloutDates(project.requestedProductInfo, 40, 'requestedProductInfo - ');
            if (requestedProductInfoError.errorCode > 0) {
                return requestedProductInfoError;
            }
        }

        if ( project.requestedSupplyInfo?.requestedSupplies?.length > 0 && !ProjectModel.isPrintSmartSupplyAcquisitionType(project)) {
            const requestedSupplyInfoError = ProjectModel.checkRolloutDatesSupplies(project.requestedSupplyInfo, 50, 'requestedSupplyInfo - ');
            if (requestedSupplyInfoError.errorCode > 0) {
                return requestedSupplyInfoError;
            }
        }


        const requestedPrintSmartDeviceContainers =
            dialogModel.requestedDevicesContainers.filter(deviceContainer => deviceContainer.itemToOrder.printSmart);

        for (const requestedProduct of project.requestedProductInfo.requestedProducts) {
            const deviceContainer = requestedPrintSmartDeviceContainers.find(
                dContainer => dContainer.itemToOrder.sku === requestedProduct.sku
            );
            if (deviceContainer && !(requestedProduct as RequestedDeviceItemModel).metaData) { // deviceContainer found, so it si printSmart
                return {
                    errorCode: 60,
                    errorMessage: 'requestedProducts.metaData is REQUIRED IF SKU.isPrintSmart == TRUE'
                };
            }
        }


        if (ProjectModel.isSupplyOrderActive(project) && !project.requestedSupplyInfo) {
            return {
                errorCode: 72,
                errorMessage: 'project not comply this condition:  requestedSupplyInfo is NULL if supplyAcquisitionType != NONE'
            };
        }

        if (ProjectModel.isSupplyOrderActive(project) &&
            project.requestedSupplyInfo.rolloutEnd
            && (project.requestedSupplyInfo.timePeriodInMonths !== undefined && project.requestedSupplyInfo.timePeriodInMonths !== null)
        ) {
            return {
                errorCode: 80,
                errorMessage: 'project not comply this condition: its either rolloutEnd OR timePeriodInMonths (NOT BOTH!!)'
            };
        }

        if (project.requestedProductInfo?.requestedProducts?.length) {
            for (const requestedProduct of project.requestedProductInfo.requestedProducts) {
                if ((requestedProduct as RequestedNotDeviceItemModel).parentSkuMapping?.length && (requestedProduct as RequestedDeviceItemModel).metaData) {
                    return {
                        errorCode: 81,
                        errorMessage: 'project not comply this condition: its either metaData OR parentSkuMapping (NOT BOTH!!)'
                    };
                }
            }

        }


        const requestedProducts =  project.requestedProductInfo?.requestedProducts;
        if (requestedProducts) {
            for (const reqProd of requestedProducts) {
                let amountSum = 0;
                const sKUMapping = (reqProd as RequestedNotDeviceItemModel).parentSkuMapping;
                if (sKUMapping?.length) {
                    for (const psm of sKUMapping) {
                        amountSum += psm.amount;
                    }
                    if (amountSum !== reqProd.amount) {
                        return {
                            errorCode: 90,
                            errorMessage: 'sum of amounts in parentSkuMapping is not equals amount in requestedProduct '
                        };
                    }
                }
            }
        }


        // PPU_log console.log('going to test requestedSupplies', project.requestedSupplyInfo);

        const requestedSupplies = project.requestedSupplyInfo?.requestedSupplies;

        if (requestedSupplies) {
            for (const reqSupply of requestedSupplies) {
                let amountSum = 0;
                const parentSkuMapping: ParentSkuMappingItemModel[] = (reqSupply as RequestedNotDeviceItemModel).parentSkuMapping;
                if (parentSkuMapping?.length) {
                    for (const psm of parentSkuMapping) {
                        amountSum += psm.amount;
                    }

                    if (amountSum !== +reqSupply.amount) {
                        return {
                            errorCode: 91,
                            errorMessage: 'sum of amounts in parentSkuMapping is not equals to amount in requestedProduct '
                        };
                    }
                }
            }
        }

        return null;
    }


    public static checkRolloutDatesSupplies(requestedInfo: RequestedSupplyInfoModel,
                                            errorBase: number,
                                            errorMsgPrefix: string) {

        const today = DateTimeUtils.getTodayDate();


        const result = {
            errorCode: 0,
            errorMessage: null
        };

        result.errorMessage = errorMsgPrefix;
        if (requestedInfo && (!requestedInfo.rolloutStart || !requestedInfo.rolloutEnd)) {
            result.errorMessage += 'some of rollout dates is not defined';
            result.errorCode = errorBase;
        } else if (requestedInfo.rolloutStart < DateTimeUtils.getNextWorkingDate(today)) {
            result.errorMessage += 'rolloutStart must be >= NEXT business day';
            result.errorCode = errorBase + 1;
        } else if (requestedInfo.rolloutStart > requestedInfo.rolloutEnd) {
            result.errorMessage += 'rolloutEnd must be >= rolloutStart';
            result.errorCode = errorBase + 2;
        } else if (
            !DateTimeUtils.isMax5YearsDifference(requestedInfo.rolloutStart, requestedInfo.rolloutEnd)) {
            result.errorMessage += '(rolloutEnd - rolloutStart) must be <= 5 years';
            result.errorCode = errorBase + 3;
        }

        return result;
    }

    public static checkRolloutDates(requestedInfo: RequestedProductInfoModel,
                                    errorBase: number,
                                    errorMsgPrefix: string) {

        const today = DateTimeUtils.getTodayDate();


        const result = {
            errorCode: 0,
            errorMessage: null
        };

        result.errorMessage = errorMsgPrefix;
        if (requestedInfo && (!requestedInfo.rolloutStart || (!requestedInfo.rolloutEnd && !requestedInfo.isOneshot))) {
            result.errorMessage += 'some of rollout dates is not defined';
            result.errorCode = errorBase;
        } else if (requestedInfo.rolloutStart < DateTimeUtils.getNextWorkingDate(today)) {
            result.errorMessage += 'rolloutStart must be >= NEXT business day';
            result.errorCode = errorBase + 1;
        } else if (requestedInfo.rolloutStart > requestedInfo.rolloutEnd && !requestedInfo.isOneshot) {
            result.errorMessage += 'rolloutEnd must be >= rolloutStart';
            result.errorCode = errorBase + 2;
        } else if (
            !DateTimeUtils.isMax5YearsDifference(requestedInfo.rolloutStart, requestedInfo.rolloutEnd) && !requestedInfo.isOneshot) {
            result.errorMessage += '(rolloutEnd - rolloutStart) must be <= 5 years';
            result.errorCode = errorBase + 3;
        }

        return result;
    }

    public static isSupplyOrderActive(project: ProjectModel): boolean {
        return project && project.supplyAcquisitionType &&
            SupplyAcquisitionTypeEnum[project.supplyAcquisitionType] !== SupplyAcquisitionTypeEnum.NONE;
    }


    // the name of enum was confusing so this method helps make the code more readable, now it is ok, so the func can be removed
    public static isSupply_PagePriceAcquisitionType(project: ProjectModel) {
        return project && project.supplyAcquisitionType &&
            SupplyAcquisitionTypeEnum[project.supplyAcquisitionType] === SupplyAcquisitionTypeEnum.MPS_BASED_ON_PAGE_PRICE;
    }
}

